import {useEffect, type ComponentProps} from "react";

import {
  useLocationCaptureBlocked,
} from "@/src/modules/core/hooks/use-geolocation";
import {geolocationService, isGeolocationPermissionDenied} from "@/src/modules/core/lib/geolocation-service";
import {
  getBlockedRecoveryIntervalMs,
  getRecoveryProbeTimeoutMs,
  isNativeMobileGeolocationPlatform,
} from "@/src/modules/core/lib/geolocation-platform";
import {WATCH_BUFFER_MAX_AGE_MOBILE_MS} from "@/src/modules/core/lib/geolocation-profiles";

export type UseCoordinatesGateOptions = {
  /** When false, skip the sheet-open permission check (e.g. sheet closed). Defaults to true. */
  active?: boolean;
  /**
   * While the gate is open, refresh the mobile submit buffer before the 30s TTL expires.
   * Use on blocking compliance gates where GPS submit can happen at any time.
   */
  keepSubmitBufferWarm?: boolean;
};

const SUBMIT_BUFFER_PREWARM_INTERVAL_MS = Math.max(
  10_000,
  Math.floor(WATCH_BUFFER_MAX_AGE_MOBILE_MS * 0.65),
);

function syncLocationPermissionForGate(): void {
  void geolocationService.syncPermissionState({
    urgent: true,
    forceProbe: true,
    probeTimeoutMs: getRecoveryProbeTimeoutMs(),
  });
}

/** Best-effort fix while the gate is open so submit can reuse the watch buffer. */
export function prewarmCoordinatesForGate(): void {
  if (isGeolocationPermissionDenied()) {
    return;
  }

  geolocationService.prepareForGatePrewarm();

  const deviceOnly = isNativeMobileGeolocationPlatform();
  void geolocationService
    .acquirePosition({
      fresh: false,
      allowCache: !deviceOnly,
      allowPropertyFallback: !deviceOnly,
      allowCoarseRetry: !deviceOnly,
    })
    .catch(() => {});
}

export function useCoordinatesGate(
  requiresCoordinates: boolean,
  options: UseCoordinatesGateOptions = {},
) {
  const {active = true, keepSubmitBufferWarm = false} = options;
  const locationCaptureBlocked = useLocationCaptureBlocked();
  // Only mobile blocks submit on denied location; desktop falls back to property coords.
  const submitBlockedByLocation =
    isNativeMobileGeolocationPlatform() && requiresCoordinates && locationCaptureBlocked;

  useEffect(() => {
    if (!isNativeMobileGeolocationPlatform() || !requiresCoordinates || !active) {
      return;
    }

    syncLocationPermissionForGate();
    prewarmCoordinatesForGate();

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        syncLocationPermissionForGate();
        prewarmCoordinatesForGate();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);

    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        syncLocationPermissionForGate();
        prewarmCoordinatesForGate();
      }
    }, getBlockedRecoveryIntervalMs());

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(intervalId);
    };
  }, [requiresCoordinates, active]);

  useEffect(() => {
    if (
      !keepSubmitBufferWarm ||
      !isNativeMobileGeolocationPlatform() ||
      !requiresCoordinates ||
      !active ||
      submitBlockedByLocation
    ) {
      return;
    }

    prewarmCoordinatesForGate();

    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        prewarmCoordinatesForGate();
      }
    }, SUBMIT_BUFFER_PREWARM_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [keepSubmitBufferWarm, requiresCoordinates, active, submitBlockedByLocation]);

  return {submitBlockedByLocation};
}

/** Prewarm when a gate sub-form opens (delay, interrupt, stay past end, etc.). */
export function useGateSubformCoordinatesPrewarm(
  active: boolean,
  subformOpen: boolean,
  submitBlockedByLocation: boolean,
) {
  useEffect(() => {
    if (!active || !subformOpen || submitBlockedByLocation) {
      return;
    }
    prewarmCoordinatesForGate();
  }, [active, subformOpen, submitBlockedByLocation]);
}

/** Attach to buttons that open a gate sub-form before GPS submit. */
export function prewarmGateCoordinatesOnPointerDown(): Pick<
  ComponentProps<"button">,
  "onPointerDown"
> {
  return {
    onPointerDown: () => prewarmCoordinatesForGate(),
  };
}
